import { useRef, useState, type FormEvent } from "react";
import { actionIssueFrom, actionIssueFromThrown, actionIssueLocaleKey, type ActionIssue } from "./action-error";

// Shared capture of the client's personal Telegram account (beta step 2 and
// the final handover fallback). A typo in the username would hand the bot to
// a stranger and BotFather transfers are permanent (D15), so the username is
// typed TWICE and must match. The ID accepts both /myid reply formats: the
// raw number and the full "number#bot_username" string.

export interface TelegramOwnershipInput {
  username: string;
  usernameRepeat: string;
  telegramId: string;
  botUsername: string | null;
  /** "bot" = BotFather transfer (username typed twice, permanent-transfer
      warning). "ownership" = instance owner role only: the meeting decided
      the my_id alone is enough there, no username, no bot warning (D15/D17). */
  mode?: "bot" | "ownership";
}

export interface TelegramOwnershipEvaluation {
  usernameValid: boolean;
  usernameMismatch: boolean;
  usernameConfirmed: boolean;
  /**
   * F115 (audit finding 8): the client typed the BOT'S own username as the new
   * owner. It is the commonest fault of this form (F112 caught it live on the
   * 2026-08-10 transfer), the page knows BOTH names, and yet the two fields
   * only checked each other - typing the same wrong name twice sailed through.
   * The server refuses it before it queues anything (`recipientIsBotItself`),
   * so the browser says the same thing one round trip earlier, over a form
   * whose own heading calls the transfer permanent.
   */
  recipientIsBot: boolean;
  idValid: boolean;
  /** The /myid suffix names a different bot than the client's own. */
  wrongBot: boolean;
  /** Raw number completed with the client's real bot username, for display. */
  normalizedId: string | null;
  /** Numeric part that gets submitted. */
  idNumber: string | null;
  submittable: boolean;
}

export function normalizeTelegramUsername(value: string): string {
  return value.trim().replace(/^@/, "");
}

// Pure form logic, exported for tests (double-confirm mismatch, dual /myid
// format, wrong-bot suffix, auto-append preview).
export function evaluateTelegramOwnership(input: TelegramOwnershipInput): TelegramOwnershipEvaluation {
  const username = normalizeTelegramUsername(input.username);
  const repeat = normalizeTelegramUsername(input.usernameRepeat);
  const usernameValid = /^[A-Za-z][A-Za-z0-9_]{4,31}$/.test(username);
  // Telegram usernames are case-insensitive; only real typos should block.
  const usernameMismatch = repeat.length > 0 && username.toLowerCase() !== repeat.toLowerCase();
  const usernameConfirmed = usernameValid && repeat.length > 0 && !usernameMismatch;
  // Telegram usernames are case-insensitive, so the comparison is too.
  const recipientIsBot = Boolean(
    input.botUsername
      && username.length > 0
      && username.toLowerCase() === normalizeTelegramUsername(input.botUsername).toLowerCase()
  );

  const idMatch = input.telegramId.trim().match(/^(\d{5,15})(?:#(.*))?$/);
  const idNumber = idMatch?.[1] ?? null;
  const idSuffix = idMatch?.[2] ?? null;
  // /myid answers "number#bot_username". A different suffix means the client
  // sent /myid to a DIFFERENT bot; the transfer would target the wrong one.
  const wrongBot = Boolean(idMatch && idSuffix && input.botUsername && idSuffix !== input.botUsername);
  const idValid = Boolean(idMatch) && !wrongBot;
  const normalizedId = idMatch && !idSuffix && input.botUsername
    ? `${idNumber}#${input.botUsername}`
    : null;

  return {
    usernameValid,
    usernameMismatch,
    usernameConfirmed,
    recipientIsBot,
    idValid,
    wrongBot,
    normalizedId,
    idNumber,
    // Bot transfer needs ONLY the username (Pavel 27.7.: the /myid number
    // belongs to the INSTANCE handover, not to BotFather); ownership needs
    // only the ID.
    submittable: input.mode === "ownership" ? idValid : usernameConfirmed && !recipientIsBot
  };
}

/**
 * What the form is doing, in the client's terms (F118).
 *
 * `saved` is its own phase and not a return to `idle`: the submit succeeded
 * AND the parent has been given its chance to refetch. Normally the parent
 * then re-renders the step in its done state and this form unmounts; when the
 * refetch itself failed the phase is what keeps the surface honest, because a
 * button that says "Odeslat týmu" again over a value the server already holds
 * is an invitation to send it a second time.
 */
export type TelegramSubmitPhase = "idle" | "sending" | "saved" | "error";

export interface TelegramSubmitRun {
  /** Performs the POST. Injected so the sequence below is testable. */
  post: () => Promise<Response>;
  /** The parent's refresh of everything this step's mark is derived from. */
  onSaved?: () => Promise<void> | void;
  setPhase: (phase: TelegramSubmitPhase) => void;
  setIssue: (issue: ActionIssue) => void;
}

/**
 * One submit, start to finish.
 *
 * 🔴 The phase stays `sending` ACROSS the parent's refresh. The old code set
 * it back to `idle` before awaiting `onSaved`, so for the whole refetch - tens
 * of seconds when the parent had to go back to the server - an enabled
 * "Odeslat týmu" sat under the client's cursor over a value that was already
 * stored. Pavel pressed it four times in five seconds and read the step as
 * broken. The submit is not finished when the server answers; it is finished
 * when the surface can show what the server now knows.
 */
export async function runTelegramOwnershipSubmit(run: TelegramSubmitRun): Promise<void> {
  run.setPhase("sending");
  let response: Response;
  try {
    response = await run.post();
  } catch {
    run.setIssue(actionIssueFromThrown());
    run.setPhase("error");
    return;
  }
  if (!response.ok) {
    run.setIssue(await actionIssueFrom(response));
    run.setPhase("error");
    return;
  }
  try {
    await run.onSaved?.();
  } catch {
    // A refused REFRESH is not a refused submit: the value is stored. Saying
    // "it failed" here would ask the client to send it again; the surface's
    // own dataErrors band reports the read that really failed.
  }
  run.setPhase("saved");
}

/** Whether the submit control may fire. `error` is the one phase a repeat can clear. */
export function canSubmitTelegramOwnership(input: {
  evaluation: TelegramOwnershipEvaluation;
  mode: "bot" | "ownership";
  started: boolean;
  phase: TelegramSubmitPhase;
}): boolean {
  if (!input.evaluation.submittable) return false;
  // Ownership mode never shows the "I opened my bot" checkbox: the client has
  // owned the bot since beta, and /myid itself proves the chat is open. The
  // checkbox only matters for the BotFather transfer precondition (D15).
  if (input.mode === "bot" && !input.started) return false;
  return input.phase === "idle" || input.phase === "error";
}

export function TelegramOwnershipForm(props: {
  csrfToken: string;
  botUsername: string | null;
  storedUsername?: string | null;
  mode?: "bot" | "ownership";
  t: (key: string) => string;
  onSaved?: () => Promise<void> | void;
}) {
  const mode = props.mode ?? "bot";
  const [username, setUsername] = useState("");
  const [usernameRepeat, setUsernameRepeat] = useState("");
  const [telegramId, setTelegramId] = useState("");
  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState<TelegramSubmitPhase>("idle");
  // N03: the transfer refuses by name (`state_conflict` when the bot already
  // changed hands, `invalid_request` on an id the server cannot read).
  const [issue, setIssue] = useState<ActionIssue>("generic");
  // 🔴 The claim is SYNCHRONOUS. React lands `phase` on the next render, so a
  // guard that only reads state lets a second click through before the button
  // is ever disabled - and this submit queues a permanent BotFather transfer.
  const sendingRef = useRef(false);

  const check = evaluateTelegramOwnership({ username, usernameRepeat, telegramId, botUsername: props.botUsername, mode });
  const canSubmit = canSubmitTelegramOwnership({ evaluation: check, mode, started, phase });
  // The whole form steps back while the value is on its way and once it is
  // stored: an editable field under a working submit is an edit that will
  // never be sent.
  const inputsLocked = phase === "sending" || phase === "saved";

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit || sendingRef.current || (mode === "ownership" && !check.idNumber)) return;
    sendingRef.current = true;
    try {
      await runTelegramOwnershipSubmit({
        post: () => fetch("/api/delivery/handover-telegram", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-Launchpad-CSRF": props.csrfToken,
            "Idempotency-Key": crypto.randomUUID()
          },
          body: JSON.stringify(mode === "ownership"
            ? { telegramId: check.idNumber, started: true }
            : { username: normalizeTelegramUsername(username), started: true })
        }),
        onSaved: props.onSaved,
        setPhase,
        setIssue
      });
    } finally {
      sendingRef.current = false;
    }
  }

  const idPlaceholder = props.botUsername
    ? props.t("transfer.idPlaceholder").replace("{bot}", props.botUsername)
    : props.t("transfer.idPlaceholderPlain");

  return (
    <form className="telegram-handle-form telegram-ownership-form" onSubmit={submit}>
      {mode === "bot" ? (
        <>
          <p className="transfer-warning" role="note">{props.t("transfer.warning")}</p>
          <label htmlFor="transfer-username">{props.t("transfer.usernameLabel")}</label>
          <input
            className="telegram-identity-input"
            id="transfer-username"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder={props.t("transfer.usernamePlaceholder")}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={inputsLocked}
          />
          <label htmlFor="transfer-username-repeat">{props.t("transfer.usernameRepeatLabel")}</label>
          <input
            className="telegram-identity-input"
            id="transfer-username-repeat"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder={props.t("transfer.usernamePlaceholder")}
            value={usernameRepeat}
            aria-invalid={check.usernameMismatch}
            onChange={(event) => setUsernameRepeat(event.target.value)}
            disabled={inputsLocked}
          />
          {check.usernameMismatch && (
            <p className="message error-message" role="alert">{props.t("transfer.usernameMismatch")}</p>
          )}
          {/* Named before the mismatch is even relevant: typing the bot's own
              name twice is consistent, and consistency is not correctness. */}
          {check.recipientIsBot && (
            <p className="message error-message" role="alert" data-transfer-recipient-is-bot="true">
              {props.t("transfer.usernameIsBot").replace("{bot}", `@${normalizeTelegramUsername(props.botUsername ?? "")}`)}
            </p>
          )}
          {check.usernameConfirmed && !check.recipientIsBot && (
            <p className="transfer-normalized" role="status">{props.t("transfer.usernameMatch")}</p>
          )}
          <p className="transfer-note">{props.t("transfer.noUsernameNote")}</p>
        </>
      ) : (
        // Ownership mode: no username, no permanent-transfer warning. The bot
        // already changed hands in beta; this only sets the owner role.
        <>
          <p className="transfer-note">{props.t("transfer.ownershipIntro")}</p>
          {props.storedUsername && (
            <p className="handover-telegram-note">
              {props.t("transfer.ownershipStored")} <strong>@{normalizeTelegramUsername(props.storedUsername)}</strong>
            </p>
          )}
        </>
      )}
      {mode === "ownership" && (
        <>
          <aside className="handover-id-guide" role="note">
            <strong>{props.t("transfer.idGuideTitle")}</strong>
            <ol>
              <li>
                {/* F105: we KNOW the bot's address, so step 1 is a real link.
                    Without the username the plain sentence stays - an honest
                    instruction beats a dead link. */}
                {props.botUsername ? (
                  <a
                    className="handover-bot-chat"
                    href={`https://t.me/${props.botUsername}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {props.t("transfer.idGuideStep1Link").replace("{bot}", `@${props.botUsername}`)}
                  </a>
                ) : (
                  props.t("transfer.idGuideStep1")
                )}
              </li>
              <li>{props.t("transfer.idGuideStep2")}</li>
              <li>{props.t("transfer.idGuideStep3")}</li>
            </ol>
          </aside>
          <label htmlFor="transfer-id">{props.t("transfer.idLabel")}</label>
          <input
            className="telegram-identity-input"
            id="transfer-id"
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            placeholder={idPlaceholder}
            value={telegramId}
            aria-invalid={check.wrongBot}
            onChange={(event) => setTelegramId(event.target.value)}
            disabled={inputsLocked}
          />
          <small className="handover-telegram-hint">{props.t("transfer.idHint")}</small>
          {check.normalizedId && (
            <p className="transfer-normalized" role="status">
              {props.t("transfer.idNormalized").replace("{value}", check.normalizedId)}
            </p>
          )}
          {check.wrongBot && (
            <p className="message error-message" role="alert">
              {props.t("transfer.idWrongBot").replace("{bot}", props.botUsername ?? "")}
            </p>
          )}
        </>
      )}
      {mode === "bot" && (
        <label className="knowhow-check">
          <input
            type="checkbox"
            checked={started}
            disabled={inputsLocked}
            onChange={(event) => setStarted(event.target.checked)}
          />
          <span>{props.t("transfer.startedCheckbox")}</span>
        </label>
      )}
      <button className="primary-button compact-button" type="submit" disabled={!canSubmit}>
        {phase === "sending" ? props.t("transfer.submitting") : props.t("transfer.submit")}
      </button>
      {/* The parent normally redraws the step in its done state and this form
          goes away. When the refetch itself failed it does not, and a silent
          inactive button would read as the F118 bug again - so the stored
          value says so itself. */}
      {phase === "saved" && <p className="message success-message" role="status">{props.t("transfer.saved")}</p>}
      {phase === "error" && <p className="message error-message" role="alert">{props.t(actionIssueLocaleKey(issue))}</p>}
    </form>
  );
}
